import {
  saveSetting,
  deleteSetting,
  createModuleTable,
  deleteModuleTable,
  getCronJobsOrg,
  createCronJobOrg,
  deleteCronJobOrg,
  buildSyncJobOrg,
  nowSyncTimestamp,
} from "./api";

/**
 * High-level flows shared by the Settings tab and the Custom Action page:
 * a module sync consists of a saved setting, a MySQL table and a scheduled
 * v3 job (cronjoborg), and the three must be created / updated / deleted
 * together.
 *
 * Recurring module-sync jobs are the v3 rows with execute_in !== 1;
 * execute_in === 1 rows are one-shot back-fill / restore / snapshot jobs.
 */

const isModuleSyncJob = (job, moduleApiName) =>
  job?.body?.moduleName === moduleApiName && job?.execute_in !== 1;

const isModuleJob = (job, moduleApiName) =>
  job?.body?.moduleName === moduleApiName;

/** Save a new module setting, create its table, and schedule its sync job. */
export const createModuleSync = async ({
  auth,
  connectionUrl,
  dataCenterUrl,
  zapiKey,
  moduleApiName,
  settingSchema,
  syncDurationMinutes,
}) => {
  const saved = await saveSetting(auth, { moduleApiName, settingSchema });
  const settingId = saved?.data?.setting_id;
  if (!settingId) {
    return { error: "Setting could not be saved", settingId: null };
  }

  const table = await createModuleTable(auth, moduleApiName, connectionUrl);
  if (table.error) {
    // roll back the setting so a broken sync is not left behind
    await deleteSetting(auth, settingId);
    return { error: table.error, settingId: null };
  }

  const jobs = (await getCronJobsOrg(auth)) || [];
  for (const job of jobs.filter((j) => isModuleSyncJob(j, moduleApiName))) {
    await deleteCronJobOrg(auth, job.job_id);
  }

  await createCronJobOrg(
    auth,
    buildSyncJobOrg({
      auth,
      dataCenterUrl,
      zapiKey,
      body: {
        moduleName: moduleApiName,
        lastFetchedTime: nowSyncTimestamp(),
        type: "sync",
        connectionUrl,
      },
      executeIn: syncDurationMinutes,
    })
  );

  return { error: null, settingId };
};

/** Update an existing module setting, re-sync its table, and reschedule its job. */
export const updateModuleSync = async ({
  auth,
  connectionUrl,
  dataCenterUrl,
  zapiKey,
  moduleApiName,
  settingId,
  settingSchema,
  syncDurationMinutes,
}) => {
  await saveSetting(auth, { moduleApiName, settingId, settingSchema });

  const table = await createModuleTable(auth, moduleApiName, connectionUrl);
  if (table.error) {
    return { error: table.error };
  }

  const jobs = (await getCronJobsOrg(auth)) || [];
  const existing = jobs.filter((j) => isModuleSyncJob(j, moduleApiName));

  // keep the incremental cursor so a cadence change doesn't re-sync everything
  const lastFetchedTime =
    existing[0]?.body?.lastFetchedTime || nowSyncTimestamp();

  for (const job of existing) {
    await deleteCronJobOrg(auth, job.job_id);
  }

  await createCronJobOrg(
    auth,
    buildSyncJobOrg({
      auth,
      dataCenterUrl,
      zapiKey,
      body: {
        moduleName: moduleApiName,
        lastFetchedTime,
        type: "sync",
        connectionUrl,
      },
      executeIn: syncDurationMinutes,
    })
  );

  return { error: null };
};

/** Delete a module setting together with its MySQL table and scheduled jobs. */
export const deleteModuleSync = async ({
  auth,
  connectionUrl,
  moduleApiName,
  settingId,
}) => {
  const deleted = await deleteSetting(auth, settingId);
  if (deleted.error) {
    return { error: deleted.error };
  }

  await deleteModuleTable(auth, moduleApiName, connectionUrl);

  const jobs = (await getCronJobsOrg(auth)) || [];
  for (const job of jobs.filter((j) => isModuleJob(j, moduleApiName))) {
    await deleteCronJobOrg(auth, job.job_id);
  }

  return { error: null };
};
